/**
 * Session Manager Utility
 * Manages user sessions and prevents cross-role access
 */

/**
 * Check if a JWT token has expired
 * @param {string} token
 * @returns {boolean}
 */
export const isJwtExpired = (token) => {
    if (!token || typeof token !== 'string') return false;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return false;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp) return false;
        return Date.now() >= payload.exp * 1000;
    } catch (e) {
        return false;
    }
};

/**
 * Parse login data from localStorage
 * @param {string} dataStr - The JSON string from localStorage
 * @returns {Object|null} - Parsed data or null
 */
export const parseLoginData = (dataStr) => {
    try {
        if (!dataStr) return null;
        const rawData = JSON.parse(dataStr);
        const parsed = rawData.data || rawData;
        const token = parsed.token || rawData.token;

        if (!token) return null;
        if (isJwtExpired(token)) {
            console.warn('[SessionManager] Detected expired JWT token');
            return null;
        }
        // Return the flattened object but keep essential data accessible
        return { ...parsed, token };
    } catch (e) {
        console.error('Error parsing login data:', e);
        return null;
    }
};

/**
 * Safely resolve hostId from host or employee session
 * @returns {number|string|null}
 */
export const getSafeHostId = () => {
    const rawHost = localStorage.getItem('hostLoginData');
    const rawEmp = localStorage.getItem('employeeLoginData');

    if (rawHost) {
        try {
            const parsed = JSON.parse(rawHost);
            const data = parsed.data || parsed;
            const id = data.hostId || data.id || data.userId || parsed.hostId || parsed.id || parsed.userId;
            if (id) return id;
        } catch (e) {}
    }

    if (rawEmp) {
        try {
            const parsed = JSON.parse(rawEmp);
            const data = parsed.data || parsed;
            const id = data.hostId || parsed.hostId;
            if (id) return id;
        } catch (e) {}
    }

    return null;
};

/**
 * Get the current logged-in role
 * @returns {'ADMIN'|'HOST'|'USER'|null}
 */
export const getCurrentRole = () => {
    const roles = [
        { key: 'adminLoginData', role: 'ADMIN' },
        { key: 'hostLoginData', role: 'HOST' },
        { key: 'employeeLoginData', role: 'HOST' },
        { key: 'userLoginData', role: 'USER' }
    ];

    for (const { key, role } of roles) {
        const dataStr = localStorage.getItem(key); // Get the raw string from localStorage
        const data = parseLoginData(dataStr); // Pass the string to the updated parseLoginData
        if (data) {
            return role;
        }
    }

    return null;
};

/**
 * Get login data for a specific role
 * @param {'ADMIN'|'HOST'|'USER'} role
 * @returns {Object|null}
 */
export const getLoginDataForRole = (role) => {
    const keyMap = {
        'ADMIN': 'adminLoginData',
        'HOST': 'hostLoginData',
        'USER': 'userLoginData'
    };

    const key = keyMap[role];
    const dataStr = key ? localStorage.getItem(key) : null; // Get the raw string
    const parsed = parseLoginData(dataStr);
    if (parsed) return parsed;

    // Fallback: If role is HOST, an EMPLOYEE session is also valid for POS/Host features
    if (role === 'HOST') {
        const empStr = localStorage.getItem('employeeLoginData');
        return parseLoginData(empStr);
    }

    return null;
};

/**
 * Clear all sessions from localStorage
 */
export const clearAllSessions = () => {
    localStorage.removeItem('adminLoginData');
    localStorage.removeItem('hostLoginData');
    localStorage.removeItem('employeeLoginData');
    localStorage.removeItem('userLoginData');
    console.log('[SessionManager] All sessions cleared');
};

/**
 * Clear sessions for other roles, keeping only the specified role
 * @param {'ADMIN'|'HOST'|'USER'} keepRole - Role to keep
 */
export const clearOtherSessions = (keepRole) => {
    const roleMap = {
        'ADMIN': 'adminLoginData',
        'HOST': 'hostLoginData',
        'USER': 'userLoginData'
    };

    Object.entries(roleMap).forEach(([role, key]) => {
        if (role !== keepRole) {
            const hadSession = localStorage.getItem(key) !== null;
            localStorage.removeItem(key);
            if (hadSession) {
                console.log(`[SessionManager] Cleared ${role} session`);
            }
        }
    });
};

/**
 * Get the dashboard path for a role
 * @param {'ADMIN'|'HOST'|'USER'} role
 * @returns {string}
 */
export const getDashboardPath = (role) => {
    const dashboardMap = {
        'ADMIN': '/admin/dashboard',
        'HOST': '/host/dashboard',
        'USER': '/user'
    };

    if (role === 'HOST') {
        const host = getLoginDataForRole('HOST');

        // Check if host has finished the onboarding step 3 (category selection)
        if (host && host.businessType === 'Pending') {
            console.log('[SessionManager] Host has not selected business category. Redirecting to onboarding step 3.');
            return '/host-register?step=3';
        }
    }

    return dashboardMap[role] || '/';
};

/**
 * Get the login path for a role
 * @param {'ADMIN'|'HOST'|'USER'} role
 * @returns {string}
 */
export const getLoginPath = (role) => {
    const loginMap = {
        'ADMIN': '/admin-login',
        'HOST': '/host-login',
        'USER': '/user-login'
    };

    return loginMap[role] || '/';
};

/**
 * Get the localStorage key for a role
 * @param {'ADMIN'|'HOST'|'USER'} role
 * @returns {string}
 */
export const getLoginDataKey = (role) => {
    const keyMap = {
        'ADMIN': 'adminLoginData',
        'HOST': 'hostLoginData',
        'USER': 'userLoginData'
    };

    return keyMap[role] || '';
};

/**
 * Check if there's a role conflict
 * @param {'ADMIN'|'HOST'|'USER'} requiredRole - Required role for the route
 * @returns {{hasConflict: boolean, currentRole: string|null, requiredRole: string}}
 */
export const checkRoleConflict = (requiredRole) => {
    const currentRole = getCurrentRole();

    // If requiredRole is HOST and an employee session exists, there is no conflict
    if (requiredRole === 'HOST' && localStorage.getItem('employeeLoginData') !== null) {
        return {
            hasConflict: false,
            currentRole: 'HOST',
            requiredRole
        };
    }

    return {
        hasConflict: currentRole !== null && currentRole !== requiredRole,
        currentRole,
        requiredRole
    };
};

/**
 * Guest Mode Management
 */
export const isGuestMode = () => {
    // If a real user is logged in, never treat them as a guest —
    // also clear any stale guestMode flag left from a previous guest session.
    try {
        const userDataStr = localStorage.getItem('userLoginData');
        if (userDataStr) {
            const rawData = JSON.parse(userDataStr);
            const userData = rawData.data || rawData;
            // Consider logged in if there's a token OR a userId/id
            const hasSession = !!(userData.token || userData.userId || userData.id);
            if (hasSession) {
                // Silently clear any stale guest flag
                localStorage.removeItem('guestMode');
                return false;
            }
        }
    } catch (e) {
        // Ignore parse errors and fall through
    }
    return localStorage.getItem('guestMode') === 'true';
};

export const setGuestMode = (enabled) => {
    if (enabled) {
        localStorage.setItem('guestMode', 'true');
    } else {
        localStorage.removeItem('guestMode');
    }
};

